import React, { useEffect, useState, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useCookieStoreContext } from '../store/useCookieStore';
import { Button } from './icons/Button';
import { Cookie } from './icons/Cookie';
import { CustomConsentCookieModal } from './CustomConsentCookieModal';

const messages = defineMessages({
  decline: {
    id: 'KVaAkH',
    defaultMessage: 'Non merci',
  },
  personalize: {
    id: 'MOaAkA',
    defaultMessage: 'Je choisis',
  },
  accept: {
    id: 'RNf0XJ',
    defaultMessage: 'Ok pour moi',
  },
  title: {
    id: 'SuWG99',
    defaultMessage: "Vos choix concernant les cookies sur le site",
  },
  description: {
    id: 'PRx0dm',
    defaultMessage:
      "En poursuivant votre navigation sur le site, vous acceptez l'utilisation des <cookie>Cookies</cookie> destinés à réaliser des statistiques de visite et la personnalisation des annonces publicitaires.",
  },
});

export const CookieConsent = React.forwardRef(
  (props: React.HTMLAttributes<HTMLDivElement>, ref: React.Ref<HTMLDivElement>) => {
    const { formatMessage } = useIntl();
    const { localStorageVariable, handleDeclineCookies, handleAcceptCookies, handleAcceptCookiesOnLoad, handleAcceptCookiesAll, othersCookiesOptions, setOthersCookiesOptions, setConsents } =
      useCookieStoreContext();

    const [showCustomConsentModal, setShowCustomConsentModal] = useState<boolean>(false);
    const [facebook, setFacebook] = useState<boolean>(true);
    const [googleAds, setGoogleAds] = useState<boolean>(true);
    const [googleAnalytics, setGoogleAnalytics] = useState<boolean>(true);
    const [microsoftAds, setMicrosoftAds] = useState<boolean>(true);

    const handleDecline = async () => {
      handleDeclineCookies();
    }

    const handleAccept = async () => {
      handleAcceptCookiesAll();
    }

    useEffect(() => {
      setOthersCookiesOptions({
        googleAds,
        googleAnalytics,
        facebookPixel: facebook,
        microsoftAds,
      });
      setConsents({
        ad_personalization: false,
        ad_storage: googleAds,
        ad_user_data: false,
        analytics_storage: googleAnalytics,
      });
    }, [facebook, googleAds, googleAnalytics, microsoftAds]);

    useEffect(() => {
      const consentGtag = {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied'
      };
      gtag("consent", "default", consentGtag);
      localStorage.setItem("isStart", "true");
      // setIsStart(true);
      const dataConsents = localStorage.getItem("consents");
      if (dataConsents) {
        handleAcceptCookiesOnLoad();
      }
    }, []);

    const toggleFacebook = () => {
      setFacebook((prev) => !prev);
      othersCookiesOptions.facebookPixel = facebook;
    };

    const toggleGoogleAds = () => {
      setGoogleAds((prev) => !prev);
      othersCookiesOptions.googleAds = googleAds;
    };

    const toggleGoogleAnalytics = () => {
      setGoogleAnalytics((prev) => !prev);
      othersCookiesOptions.googleAnalytics = googleAnalytics;
    };

    const toggleMicrosoftAds = () => {
      setMicrosoftAds((prev) => !prev);
      othersCookiesOptions.microsoftAds = microsoftAds;
    };

    const closeCustomConsentModal = () => {
      setShowCustomConsentModal(false);
    };

    const openCustomConsentModal = async () => {
      setShowCustomConsentModal(true);
    };

    const handleAcceptAllCookies = async () => {
      handleAcceptCookies();
      setShowCustomConsentModal(false);
    };

    return localStorageVariable === null ? (
      <div>
        {showCustomConsentModal && (
          <CustomConsentCookieModal
            facebook={facebook}
            googleAds={googleAds}
            googleAnalytics={googleAnalytics}
            microsoftAds={microsoftAds}
            toggleFacebook={toggleFacebook}
            toggleGoogleAds={toggleGoogleAds}
            toggleGoogleAnalytics={toggleGoogleAnalytics}
            toggleMicrosoftAds={toggleMicrosoftAds}
            closeCustomConsentModal={closeCustomConsentModal}
            handleAcceptAllCookies={handleAcceptAllCookies}
          />
        )}
        <div
          ref={ref}
          className="fixed bottom-0 z-50 flex w-screen justify-center bg-white shadow-[0px_-3px_12px_0px_#00000024]"
          {...props}
        >
          <div className="flex max-w-[90%] flex-1 flex-col items-center justify-between gap-4 self-stretch p-4 sm:flex-row sm:gap-8">
            <div className="flex flex-col">
              <div className="text-primaryNewComparateurFibre font-bold flex flex-row items-center gap-1 text-sm">
                {formatMessage(messages.title, {
                  b: (chunk: ReactNode) => <b>{chunk}</b>,
                  nbsp: <span key="nbsp">&nbsp;</span>,
                })}
                <Cookie className="h-4 w-4" />
              </div>
              <div className="flex flex-col gap-1">
                <div className="text-primary text-xs">
                  {formatMessage(messages.description, {
                    policy: (chunk: ReactNode) => (
                      <a
                        href={`${location.origin}/politique_de_confidentialite`}
                        key="policy"
                        target="_blank"
                        className="z-50 text-[#039DE0]"
                      >
                        {chunk}
                      </a>
                    ),
                    cookie: (chunk: ReactNode) => (
                      <a
                        href={`${location.origin}/gestion_des_cookies`}
                        key="cookie"
                        target="_blank"
                        className="z-50 text-[#039DE0]"
                      >
                        {chunk}
                      </a>
                    ),
                  })}
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 flex flex-col md:flex-row md:items-center lg:justify-end lg:w-[22%] gap-2 self-stretch">
              <Button className="flex-shrink-0 w-full flex items-center justify-center md:w-auto bg-orange-600 md:bg-primaryNewComparateurFibre py-4 md:py-3 lg:py-2.5 text-[13px] px-8 lg:px-6 hover:bg-white hover:text-primaryNewComparateurFibre hover:opacity-90 hover:duration-300 normal-case" onClick={handleDecline}>
                {formatMessage(messages.accept, {
                  span: (chunk: ReactNode) => <span className="uppercase lg:lowercase">{chunk}</span>,
                })}
              </Button>
              <div className="flex justify-between md:items-center md:justify-start w-full md:gap-x-2.5 lg:gap-x-2">
                <Button className="bg-primaryNewComparateurFibre text-white lg:order-2 w-[49%] md:w-auto flex items-center justify-center flex-shrink-0 py-4 md:py-3 lg:py-2.5 text-[13px] px-8 lg:px-6 hover:bg-white hover:text-primaryNewComparateurFibre hover:opacity-90 hover:duration-300 normal-case" onClick={handleAccept}>
                  {formatMessage(messages.decline, {
                    span: (chunk: ReactNode) => <span className="uppercase lg:lowercase">{chunk}</span>,
                  })}
                </Button>
                <Button className="lg:order-1 w-[49%] md:w-auto flex items-center justify-center flex-shrink-0 py-2 md:py-2.5 lg:py-2 text-[13px] px-8 lg:px-6 normal-case bg-white text-primaryNewComparateurFibre hover:bg-primaryNewComparateurFibre hover:duration-300 hover:ease-linear hover:text-white border-primaryNewComparateurFibre border-[3px]" onClick={openCustomConsentModal}>
                  {formatMessage(messages.personalize, {
                    span: (chunk: ReactNode) => <span className="uppercase lg:lowercase">{chunk}</span>,
                  })}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : null;
  },
);
